import { ReactElement, useCallback, useState } from 'react';
import { useAtom } from 'jotai';
import { isNull } from 'lodash-es';
import { Accordion, Box, Flex, Tag, Text } from '@chakra-ui/react';

import { useOpenClose } from '~/core/hooks/useOpenClose';
import {
  DocumentFormBlock,
  Nullable,
  TenantIconsMap,
} from '~/core/shared/types';
import { isDirtyAtom } from '../../contexts/dirty.context';
import { TenantIcon } from '../TenantIcon.component';
import { DisplayActions } from './SidebarPanel.component';
import { UnsavedModal } from './UnsavedModal.component';

type BuildSidebarTypes = {
  blockData: DocumentFormBlock[];
  icons: Nullable<TenantIconsMap>;
  selected: DocumentFormBlock | undefined;
  setSelected: (block: DocumentFormBlock) => void;
};

export const SidebarBuilder = ({
  blockData,
  icons,
  selected,
  setSelected,
}: BuildSidebarTypes): ReactElement => {
  const [isDirty] = useAtom(isDirtyAtom);
  const unsavedChangesModalApi = useOpenClose();
  const [blockToSet, setBlockToSet] = useState<DocumentFormBlock>();

  const itemTitle = useCallback((block: DocumentFormBlock): ReactElement => {
    if (block?.meta?.subtitle) {
      return (
        <>
          {block?.meta?.subtitle}
          <Text as="div" color="gray.700" fontSize="sm">
            {block?.meta?.title}
          </Text>
        </>
      );
    }
    return (
      <>
        {block?.meta?.title}
        <Text as="div" color="gray.700" fontSize="sm">
          {block?.meta?.subtitle}
        </Text>
      </>
    );
  }, []);

  const buildAccordion = (
    block: DocumentFormBlock,
    props: any,
    isSidebarDirty: boolean,
  ): ReactElement => {
    const hasNestedBlocks = block.blocks.length > 0;

    const nestedBlockProps = {
      borderLeft: '2px solid',
      borderLeftColor: 'gray.300',
    };

    const clickActions = block.meta.actions.filter(
      (action) => action.trigger === 'click',
    );
    let onClickHandler = (documentFormBlock: DocumentFormBlock): void => {
      // do nothing by default
    };

    const hasMainPanelAction =
      clickActions.length > 0 && clickActions[0].key === 'main_panel';
    if (hasMainPanelAction) {
      onClickHandler = (documentFormBlock: DocumentFormBlock): void => {
        if (isSidebarDirty && selected?.meta.id !== documentFormBlock.meta.id) {
          unsavedChangesModalApi.onOpen();
          setBlockToSet(documentFormBlock);
        } else {
          setSelected(documentFormBlock);
        }
      };
    } else if (clickActions.length > 0) {
      const clickAction = clickActions[0];
      if (clickAction.handler === 'modal') {
        // @TODO: run logic to open action modal
      }
    }

    const buttonProps = {
      cursor: 'pointer',
      onClick: () => onClickHandler(block),
    };

    const isSelected = selected?.meta.id === block.meta.id;

    return (
      <Accordion.Item {...props} key={block.meta.id} value={block.meta.id}>
        <Flex
          alignItems="center"
          as="h2"
          bgColor={isSelected ? '#EDF2F7' : undefined}
          pr={4}
        >
          <Accordion.ItemTrigger {...buttonProps} p={4}>
            <Box
              alignItems="center"
              as="span"
              display="flex"
              flex="1"
              flexDirection="row"
              textAlign="left"
            >
              <Flex mr={2}>
                <TenantIcon color="primary.500" name={block.meta.icon} />
              </Flex>
              {!!block?.meta?.count && (
                <Tag.Root mx={2}>
                  <Tag.Label>
                    {!isNull(block?.meta?.count)
                      ? block?.meta?.count
                      : block.blocks.length}
                  </Tag.Label>
                </Tag.Root>
              )}
              <Text as="div" flexGrow={1}>
                {itemTitle(block)}
              </Text>
              {hasNestedBlocks ? <Accordion.ItemIndicator /> : null}
            </Box>
          </Accordion.ItemTrigger>
          {block.meta.actions.length > 0 &&
            DisplayActions(block?.meta?.actions, block)}
        </Flex>
        {hasNestedBlocks && (
          <Accordion.ItemContent>
            <Accordion.ItemBody pb={4} pr={0}>
              {block.blocks.map((documentFormBlock: DocumentFormBlock) =>
                buildAccordion(
                  documentFormBlock,
                  nestedBlockProps,
                  isSidebarDirty,
                ),
              )}
            </Accordion.ItemBody>
          </Accordion.ItemContent>
        )}
      </Accordion.Item>
    );
  };

  return (
    <>
      <Accordion.Root multiple>
        {blockData?.map((block, index) =>
          buildAccordion(
            block,
            {
              key: index + block.meta.title,
            },
            isDirty,
          ),
        )}
      </Accordion.Root>

      {unsavedChangesModalApi.open && selected && (
        <UnsavedModal
          close={() => {
            unsavedChangesModalApi.onClose();
            if (blockToSet) {
              setSelected(blockToSet);
            }
          }}
          modalApi={unsavedChangesModalApi}
        />
      )}
    </>
  );
};
